import { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getUserDetails, updateUserDetails } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import { OnboardingContext } from '../../context/OnboardingContext';
import { useCurrentUser } from '../../hooks';
import InformationPanel from '../../ui/Dashboard/Settings/InformationPanel';
import TariffForm from '../../ui/Form/TariffForm';
import ErrorModal from '../../ui/Modal/EditTariff/ErrorModal';
import SuccessModal from '../../ui/Modal/EditTariff/SuccessModal';
import { StyledHr, StyledWarningBox } from '../../ui/Styled';
import { getCurrentUserInfo } from '../../utils/auth';
import { getFinalExportTariffId } from '../../utils/tariffUtils';
import textOrPlaceholder from '../../utils/textOrPlaceholder';

const EditTariffContainer = () => {
  const history = useHistory();
  const { setImportTariffId, setExportTariffId } = useContext(OnboardingContext);
  const { userDetails, setUserDetails, userSub } = useContext(AppContext);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const next = searchParams.get('next');
  const { isAdmin } = useCurrentUser();
  const [userHasSmartTariffBeforeEdit, setUserHasSmartTariffBeforeEdit] = useState(false);

  useEffect(() => {
    getUserDetails(userSub).then((res) => {
      setUserDetails(res);
      setUserHasSmartTariffBeforeEdit(res?.has_smart_tariff);
    });
  }, []);

  const returnPath = next || '/app/settings';

  const onReturn = () => {
    if (!userHasSmartTariffBeforeEdit) {
      history.push(`/edit/smart-tariff-switch-warning?next=${returnPath}`);
    } else {
      history.push(returnPath);
    }
  };

  const onSelectTariff = async ({ importTariffId, exportTariffId, exportTariffs }) => {
    setImportTariffId(importTariffId);
    setExportTariffId(exportTariffId);

    const userInfo = await getCurrentUserInfo();
    const postcode = userInfo?.attributes?.['custom:postcode'];
    const finalExportTariffId = await getFinalExportTariffId(exportTariffId, postcode, exportTariffs);

    updateUserDetails({
      import_tariff_id: importTariffId,
      export_tariff_id: finalExportTariffId,
    })
      .then(() => setShowSuccessModal(true))
      .catch((error) => {
        setShowErrorModal(true);
        console.error(error);
      });
  };

  return (
    <>
      <SuccessModal show={showSuccessModal} onNext={onReturn} />
      <ErrorModal
        show={showErrorModal}
        onNext={() => setShowErrorModal(false)}
        onCancel={onReturn}
        onSupport={() => (window.location.href = 'https://kb.loop.homes/kb-tickets/new')}
      />
      <h1 className="mb-1">Your tariff details</h1>
      {userDetails?.has_smart_tariff && (
        <InformationPanel title="Your current tariff details">
          <div className="text-left p-2">
            <p className="mt-1">
              <strong>Import energy provider:</strong>{' '}
              {textOrPlaceholder(
                userDetails?.tariffs.import_tariff.supplier,
                userDetails?.tariffs.import_tariff.supplier
              )}
            </p>
            <p>
              <strong>Import tariff:</strong>{' '}
              {textOrPlaceholder(
                userDetails?.tariffs.import_tariff.tariff_name,
                userDetails?.tariffs.import_tariff.tariff_name
              )}
            </p>
            <StyledHr />
            <p>
              <strong>Export energy provider:</strong>{' '}
              {textOrPlaceholder(
                userDetails?.tariffs.export_tariff.supplier,
                userDetails?.tariffs.export_tariff.supplier
              )}
            </p>
            <p className="mb-1">
              <strong>Export tariff:</strong>{' '}
              {textOrPlaceholder(
                userDetails?.tariffs.export_tariff.tariff_name,
                userDetails?.tariffs.export_tariff.tariff_name
              )}
            </p>
          </div>
        </InformationPanel>
      )}
      <div className="text-left mb-0 pb-2 mt-4">
        <strong>Update your tariff details</strong>
      </div>
      {!isAdmin ? (
        <TariffForm onSubmit={onSelectTariff} setInitialValues={false} cancelPath={returnPath} />
      ) : (
        <StyledWarningBox className="mt-1">Admin users cannot currently edit tariff details.</StyledWarningBox>
      )}
    </>
  );
};

export default EditTariffContainer;
